import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface PromoCode {
  id: string;
  code: string;
  discount_percent: number;
  bonus_stars: number;
  bonus_days: number;
  valid_from: string;
  valid_until: string | null;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePromoCodeInput {
  code: string;
  discount_percent: number;
  bonus_stars?: number;
  bonus_days?: number;
  valid_until?: string | null;
  max_uses?: number | null;
  description?: string;
}

export function usePromoCodes() {
  const { user } = useAuth();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPromoCodes = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPromoCodes((data as PromoCode[]) || []);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const createPromoCode = async (input: CreatePromoCodeInput): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('promo_codes')
        .insert({
          code: input.code.toUpperCase(),
          discount_percent: input.discount_percent,
          bonus_stars: input.bonus_stars || 0,
          bonus_days: input.bonus_days || 0,
          valid_until: input.valid_until || null,
          max_uses: input.max_uses || null,
          description: input.description || null,
          created_by: user.id,
        });

      if (error) throw error;

      toast.success('Промо-код создан');
      fetchPromoCodes();
      return true;
    } catch (error: any) {
      console.error('Error creating promo code:', error);
      if (error.code === '23505') {
        toast.error('Промо-код с таким именем уже существует');
      } else {
        toast.error('Ошибка создания промо-кода');
      }
      return false;
    }
  };

  const updatePromoCode = async (id: string, updates: Partial<PromoCode>): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('promo_codes')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast.success('Промо-код обновлён');
      fetchPromoCodes();
      return true;
    } catch (error) {
      console.error('Error updating promo code:', error);
      toast.error('Ошибка обновления промо-кода');
      return false;
    }
  };

  const deletePromoCode = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Промо-код удалён');
      setPromoCodes(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast.error('Ошибка удаления промо-кода');
      return false;
    }
  };

  const togglePromoCode = async (id: string, isActive: boolean): Promise<boolean> => {
    return updatePromoCode(id, { is_active: isActive });
  };

  // Promo code validation is performed server-side inside the redeem_promo_code
  // RPC. Client-side enumeration of promo_codes is no longer permitted by RLS.
  const validatePromoCode = async (_code: string): Promise<PromoCode | null> => {
    // Kept for API compatibility — callers should use redeemPromoCode instead.
    console.warn('validatePromoCode is deprecated; use redeemPromoCode');
    return null;
  };

  const usePromoCode = async (promoCodeId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Record usage
      const { error: useError } = await supabase
        .from('promo_code_uses')
        .insert({
          promo_code_id: promoCodeId,
          user_id: user.id,
        });

      if (useError) {
        if (useError.code === '23505') {
          toast.error('Вы уже использовали этот промо-код');
        }
        throw useError;
      }

      // Increment usage counter - get current value first
      const { data: currentPromo } = await supabase
        .from('promo_codes')
        .select('current_uses')
        .eq('id', promoCodeId)
        .single();
      
      if (currentPromo) {
        const { error: updateError } = await supabase
          .from('promo_codes')
          .update({ current_uses: (currentPromo.current_uses || 0) + 1 })
          .eq('id', promoCodeId);

        if (updateError) throw updateError;
      }

      toast.success('Промо-код активирован!');
      return true;
    } catch (error) {
      console.error('Error using promo code:', error);
      return false;
    }
  };

  // Atomic redemption via DB function — validates, records usage, increments counter,
  // applies bonus stars/days, and logs referrer attribution in one transaction.
  const redeemPromoCode = async (code: string): Promise<{
    success: boolean;
    discount_percent?: number;
    bonus_stars?: number;
    bonus_days?: number;
    promo_code_id?: string;
    error?: string;
  }> => {
    if (!user) return { success: false, error: 'not_authenticated' };

    try {
      const { data, error } = await supabase.rpc('redeem_promo_code', {
        p_code: code.toUpperCase(),
        p_user_id: user.id,
      });

      if (error) {
        console.error('Error redeeming promo code:', error);
        toast.error('Ошибка активации промо-кода');
        return { success: false, error: 'rpc_error' };
      }

      const result = data as any;

      if (!result?.success) {
        const errorMessages: Record<string, string> = {
          not_found: 'Промо-код не найден или недействителен',
          expired: 'Срок действия промо-кода истёк',
          exhausted: 'Промо-код исчерпан',
          already_used: 'Вы уже использовали этот промо-код',
        };
        toast.error(errorMessages[result?.error] || 'Промо-код недействителен');
        return { success: false, error: result?.error };
      }

      const msgs: string[] = ['Промо-код активирован!'];
      if (result.bonus_stars > 0) msgs.push(`+${result.bonus_stars} ⭐`);
      if (result.bonus_days > 0) msgs.push(`+${result.bonus_days} дней подписки`);
      toast.success(msgs.join(' '));

      return {
        success: true,
        discount_percent: result.discount_percent,
        bonus_stars: result.bonus_stars,
        bonus_days: result.bonus_days,
        promo_code_id: result.promo_code_id,
      };
    } catch (error) {
      console.error('Error redeeming promo code:', error);
      toast.error('Ошибка активации промо-кода');
      return { success: false, error: 'unknown' };
    }
  };

  return {
    promoCodes,
    loading,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    togglePromoCode,
    validatePromoCode,
    usePromoCode,
    redeemPromoCode,
    refetch: fetchPromoCodes,
  };
}
